import AddIcon from '@mui/icons-material/Add';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import TvIcon from '@mui/icons-material/Tv';
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Select,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { green, orange, red } from '@mui/material/colors';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
	Title,
	useDataProvider,
	useGetList,
	useGetOne,
	usePermissions,
	useRefresh,
} from 'react-admin';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import { getAttendantId, getChainId, getEmployeeType, getPlaceId } from '../../lib';
import { EmployeeType } from '../../models';
import { autoSelectBoardIds, parseBoardIds, parsePlaceId } from './monitorLink';

// ─────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────

const getProgressPct = (value: any, target: any) => {
	const v = Number(value); const t = Number(target);
	if (!t || t <= 0) return v > 0 ? 100 : 0;
	return Math.min((v / t) * 100, 100);
};

const getProgressTint = (value: any, target: any) => {
	const v = Number(value); const t = Number(target);
	if (v >= t) return green[500];
	if (v >= t * 0.6) return orange[500];
	return red[500];
};

const fmtNum = (n: any) => {
	const num = Number(n);
	if (n == null || n === '' || isNaN(num)) return '--';
	return Number.isInteger(num) ? String(num) : num.toFixed(1);
};

// Score não limitado a 100% para ordenar corretamente quem ultrapassou a meta
const getAttendantScore = (record: any) => {
	const active = METRICS.filter(
		m => record[m.valueKey] !== undefined && record[m.targetKey] !== undefined,
	);
	if (active.length === 0) return 0;
	const sum = active.reduce((acc, m) => {
		const v = Number(record[m.valueKey]);
		const t = Number(record[m.targetKey]);
		if (!t || t <= 0) return acc + (v > 0 ? 9999 : 0);
		return acc + v / t;
	}, 0);
	return sum / active.length;
};

const MAX_BOARDS = 5;

const DAY_SCHEDULE_KEYS = [
	'scheduleSunday',
	'scheduleMonday',
	'scheduleTuesday',
	'scheduleWednesday',
	'scheduleThursday',
	'scheduleFriday',
	'scheduleSaturday',
] as const;

const METRICS = [
	{ valueKey: 'quantityValue', targetKey: 'quantityTargetMinimumAmount', label: 'Quantidade', unit: undefined },
	{ valueKey: 'volumeValue', targetKey: 'volumeTargetMinimumAmount', label: 'Volume', unit: undefined },
	{ valueKey: 'percentageLoyaltyValue', targetKey: 'percentageLoyaltyTargetMinimumAmount', label: '% Fidelidade', unit: '%' },
	{ valueKey: 'mixValue', targetKey: 'mixTargetMinimumAmount', label: 'Mix', unit: '%' },
	{ valueKey: 'fullTankValue', targetKey: 'fullTankTargetMinimumAmount', label: 'Tanque Cheio', unit: undefined },
] as const;

// ─────────────────────────────────────────
// MetricRow — one metric progress bar
// ─────────────────────────────────────────

interface MetricRowProps {
	label: string;
	value: number;
	target: number;
	unit?: string;
	isTvMode: boolean;
}

const MetricRow = ({ label, value, target, unit, isTvMode }: MetricRowProps) => {
	const v = Number(value); const t = Number(target);
	const pct = getProgressPct(v, t);
	const tint = getProgressTint(v, t);
	const achieved = t > 0 && v >= t;

	return (
		<Box sx={{ mb: isTvMode ? 0.8 : 0.9 }}>
			<Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.2}>
				<Box>
					<Typography
						sx={{
							fontSize: isTvMode ? '0.62rem' : '0.68rem',
							color: achieved
								? (isTvMode ? 'rgba(255,255,255,0.7)' : 'text.secondary')
								: (isTvMode ? 'rgba(255,255,255,0.45)' : 'text.secondary'),
							textTransform: 'uppercase',
							letterSpacing: '0.06em',
							fontWeight: 500,
						}}
					>
						{label}
					</Typography>
				</Box>
				<Typography
					sx={{
						fontSize: isTvMode ? '0.72rem' : '0.78rem',
						fontWeight: 700,
						color: achieved ? tint : (isTvMode ? 'rgba(255,255,255,0.9)' : 'text.primary'),
						fontVariantNumeric: 'tabular-nums',
					}}
				>
					{fmtNum(v)}{unit}
					<Typography component="span" sx={{ mx: 0.4, opacity: 0.5, fontWeight: 400 }}>/</Typography>
					{fmtNum(t)}{unit}
					{achieved && (
						<Typography component="span" sx={{ ml: 0.5, color: tint, fontSize: isTvMode ? '0.68rem' : '0.72rem' }}>✓</Typography>
					)}
				</Typography>
			</Box>
			<Box
				sx={{
					position: 'relative',
					height: isTvMode ? 6 : 5,
					borderRadius: 99,
					overflow: 'hidden',
					bgcolor: isTvMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
					...(achieved && isTvMode && {
						boxShadow: `0 0 10px 0px ${tint}55`,
					}),
				}}
			>
				<Box
					sx={{
						position: 'absolute',
						left: 0,
						top: 0,
						bottom: 0,
						width: `${pct}%`,
						bgcolor: tint,
						borderRadius: 99,
						transition: 'width 0.6s ease',
						...(achieved && {
							boxShadow: `0 0 8px 1px ${tint}99`,
						}),
					}}
				/>
			</Box>
		</Box>
	);
};

// ─────────────────────────────────────────
// AttendantRow — one attendant with metrics
// ─────────────────────────────────────────

const AttendantRow = ({ record, isTvMode }: { record: any; isTvMode: boolean }) => {
	const activeMetrics = METRICS.filter(
		m => record[m.valueKey] !== undefined && record[m.targetKey] !== undefined,
	);
	const allAchieved = activeMetrics.length > 0 && activeMetrics.every(m => Number(record[m.valueKey]) >= Number(record[m.targetKey]));

	return (
		<Box
			sx={{
				py: isTvMode ? 0.8 : 1.2,
				px: isTvMode ? 1 : 1,
				mx: isTvMode ? -1 : -1,
				borderRadius: 1.5,
				transition: 'background 0.4s ease',
				...(allAchieved && isTvMode && {
					bgcolor: 'rgba(76,175,80,0.08)',
					borderLeft: `2px solid ${green[500]}`,
					pl: 'calc(8px - 2px)',
				}),
				...(allAchieved && !isTvMode && {
					bgcolor: green[50],
					borderLeft: `3px solid ${green[400]}`,
					pl: 'calc(8px - 3px)',
				}),
			}}
		>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={isTvMode ? 0.5 : 0.7}>
				<Typography
					sx={{
						fontWeight: 700,
						fontSize: isTvMode ? '0.82rem' : '0.88rem',
						color: allAchieved
							? (isTvMode ? green[300] : green[800])
							: (isTvMode ? 'white' : 'text.primary'),
						lineHeight: 1.2,
					}}
				>
					{record.attendantName}
				</Typography>
				{allAchieved && (
					<Chip
						label={isTvMode ? '🏆' : 'Meta atingida! 🏆'}
						size="small"
						sx={{
							bgcolor: isTvMode ? 'rgba(76,175,80,0.25)' : green[100],
							color: isTvMode ? green[300] : green[800],
							fontWeight: 800,
							fontSize: isTvMode ? '0.62rem' : '0.62rem',
							height: isTvMode ? 20 : 18,
							border: `1px solid ${isTvMode ? green[700] : green[300]}`,
							boxShadow: isTvMode ? `0 0 8px 0px ${green[500]}55` : 'none',
						}}
					/>
				)}
			</Box>
			{activeMetrics.map(m => (
				<MetricRow
					key={m.valueKey}
					label={m.label}
					value={record[m.valueKey] ?? 0}
					target={record[m.targetKey] ?? 0}
					unit={m.unit}
					isTvMode={isTvMode}
				/>
			))}
		</Box>
	);
};

// ─────────────────────────────────────────
// BoardPanel — one full board card
// ─────────────────────────────────────────

interface BoardPanelProps {
	boardId: string;
	stationId: string;
	date: string;
	chainId: string;
	showWithoutProgress: boolean;
	onRemove?: () => void;
	isTvMode: boolean;
}

const BoardPanel = ({ boardId, stationId, date, chainId, showWithoutProgress, onRemove, isTvMode }: BoardPanelProps) => {
	const scrollRef = useRef<HTMLDivElement>(null);

	// Auto-scroll in TV mode: slow scroll down, 3s pause at top and bottom
	useEffect(() => {
		if (!isTvMode) return;
		let paused = true; // start paused so first items are visible
		let pauseTimeout: ReturnType<typeof setTimeout> | null = null;

		// Initial pause before starting to scroll
		pauseTimeout = setTimeout(() => { paused = false; }, 5000);

		const interval = setInterval(() => {
			const el = scrollRef.current;
			if (!el || paused) return;
			const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
			if (atBottom && el.scrollHeight > el.clientHeight) {
				paused = true;
				pauseTimeout = setTimeout(() => {
					if (el) el.scrollTop = 0;
					// Pause at top too
					pauseTimeout = setTimeout(() => { paused = false; }, 3000);
				}, 3000);
			} else {
				el.scrollTop += 1;
			}
		}, 60);

		return () => {
			clearInterval(interval);
			if (pauseTimeout) clearTimeout(pauseTimeout);
		};
	}, [isTvMode]);

	const { data: boardInfo } = useGetOne('attendant-goal-boards', { id: boardId });
	const { data: records, isFetching, meta } = useGetList(
		'attendant-goal-boards/monitor',
		{
			filter: {
				attendantGoalBoardId: boardId,
				stationId,
				date,
				chainId,
				showAttendantsWithoutProgress: showWithoutProgress,
			},
			pagination: { page: 1, perPage: 1000 },
			sort: { field: 'attendantName', order: 'ASC' },
		},
		{ enabled: !!(boardId && stationId && date) },
	);

	// Módulo vencido: o backend responde 200 com lista vazia e `meta.moduleInactive` (o useGetList só repassa `meta`, não chaves do topo do JSON).
	const moduleInactive = (meta as { moduleInactive?: boolean } | undefined)?.moduleInactive === true;

	const achievedCount = (records || []).filter(r =>
		METRICS.filter(m => r[m.valueKey] !== undefined).every(m => Number(r[m.valueKey]) >= Number(r[m.targetKey]))
	).length;
	const totalCount = records?.length ?? 0;

	return (
		<Card
			sx={{
				flex: 1,
				minWidth: 0,
				display: 'flex',
				flexDirection: 'column',
				borderRadius: 2,
				overflow: 'hidden',
				...(isTvMode
					? {
						bgcolor: '#1a1d27',
						border: '1px solid rgba(255,255,255,0.07)',
						boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
					}
					: {
						boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
						border: '1px solid rgba(0,0,0,0.06)',
					}
				),
			}}
		>
			{/* ── Header ── */}
			<Box
				sx={{
					background: 'linear-gradient(135deg, #7869DF 0%, #4f5cc9 100%)',
					px: isTvMode ? 1.5 : 2,
					py: isTvMode ? 1 : 1.5,
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'flex-start',
					flexShrink: 0,
				}}
			>
				<Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
					<Typography
						sx={{
							color: 'white',
							fontWeight: 800,
							fontSize: isTvMode ? '0.9rem' : '1rem',
							lineHeight: 1.2,
							textShadow: '0 1px 3px rgba(0,0,0,0.3)',
						}}
					>
						{boardInfo ? `${boardInfo.code} - ${boardInfo.name}` : '...'}
					</Typography>
					<Box display="flex" alignItems="center" gap={1} mt={0.4}>
						<Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: isTvMode ? '0.68rem' : '0.72rem' }}>
							{dayjs(date).format('DD/MM/YYYY')}
						</Typography>
						{totalCount > 0 && (
							<Chip
								label={`${achievedCount}/${totalCount} atingido${achievedCount !== 1 ? 's' : ''}`}
								size="small"
								sx={{
									bgcolor: achievedCount === totalCount ? 'rgba(76,175,80,0.35)' : 'rgba(255,255,255,0.15)',
									color: achievedCount === totalCount ? green[200] : 'rgba(255,255,255,0.85)',
									fontWeight: 700,
									fontSize: '0.65rem',
									height: 18,
								}}
							/>
						)}
					</Box>
				</Box>
				<Box display="flex" alignItems="center" gap={0.5}>
					{isFetching && <CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.6)' }} />}
					{!isTvMode && onRemove && (
						<Tooltip title="Remover quadro">
							<IconButton
								size="small"
								onClick={onRemove}
								sx={{ color: 'rgba(255,255,255,0.7)', width: 28, height: 28, borderRadius: '50%', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.15)' } }}
							>
								<Typography fontSize="1rem" lineHeight={1}>×</Typography>
							</IconButton>
						</Tooltip>
					)}
				</Box>
			</Box>

			{/* ── Content ── */}
			<CardContent
				ref={scrollRef}
				sx={{
					flex: 1,
					overflowY: 'auto',
					px: isTvMode ? 1.5 : 2,
					py: isTvMode ? 1 : 0.5,
					'&:last-child': { pb: isTvMode ? 1 : 1 },
					...(isTvMode && {
						'&::-webkit-scrollbar': { width: 4 },
						'&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 },
					}),
				}}
			>
				{!records || records.length === 0 ? (
					<Box sx={{ py: 6, textAlign: 'center' }}>
						<Typography
							sx={{
								color: isTvMode ? 'rgba(255,255,255,0.3)' : 'text.secondary',
								fontSize: isTvMode ? '0.8rem' : '0.85rem',
							}}
						>
							{moduleInactive ? 'Módulo desativado' : isFetching ? 'Carregando...' : 'Nenhum frentista encontrado'}
						</Typography>
					</Box>
				) : (
					[...(records || [])].sort((a, b) => getAttendantScore(b) - getAttendantScore(a)).map((record, idx) => (
						<Box key={record.id}>
							{idx > 0 && (
								<Divider sx={{ borderColor: isTvMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.1)' }} />
							)}
							<AttendantRow record={record} isTvMode={isTvMode} />
						</Box>
					))
				)}
			</CardContent>
		</Card>
	);
};

// ─────────────────────────────────────────
// AddBoardDialog
// ─────────────────────────────────────────

interface AddBoardDialogProps {
	open: boolean;
	onClose: () => void;
	onAdd: (boardId: string) => void;
	stationId: string;
	date: string;
	chainId: string;
	addedBoardIds: string[];
}

const AddBoardDialog = ({ open, onClose, onAdd, stationId, date, chainId, addedBoardIds }: AddBoardDialogProps) => {
	const [selectedId, setSelectedId] = useState('');

	const { data: boards } = useGetList(
		'attendant-goal-boards',
		{
			filter: stationId && date ? { stationId, date, chainId } : { chainId },
			pagination: { page: 1, perPage: 100 },
			sort: { field: 'name', order: 'ASC' },
		},
		{ enabled: open && !!stationId },
	);

	const available = (boards || []).filter(b => !addedBoardIds.includes(b.id));

	const handleConfirm = () => {
		if (selectedId) {
			onAdd(selectedId);
			setSelectedId('');
			onClose();
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Adicionar Quadro</DialogTitle>
			<DialogContent>
				<FormControl fullWidth sx={{ mt: 1 }}>
					<InputLabel>Quadro</InputLabel>
					<Select value={selectedId} label="Quadro" onChange={e => setSelectedId(e.target.value)}>
						{available.length === 0 ? (
							<MenuItem disabled value="">
								{boards ? 'Nenhum quadro disponível para esta data/posto' : 'Carregando...'}
							</MenuItem>
						) : (
							available.map(b => (
								<MenuItem key={b.id} value={b.id}>
									{b.code} - {b.name}
								</MenuItem>
							))
						)}
					</Select>
				</FormControl>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancelar</Button>
				<Button onClick={handleConfirm} variant="contained" disabled={!selectedId}>
					Adicionar
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// ─────────────────────────────────────────
// TvHeader
// ─────────────────────────────────────────

interface TvHeaderProps {
	date: string;
	stationId: string;
	onExit: () => void;
	countdown: number;
}

const TvHeader = ({ date, stationId, onExit, countdown }: TvHeaderProps) => {
	const [now, setNow] = useState(new Date());
	const { data: station } = useGetOne('stations', { id: stationId }, { enabled: !!stationId });

	useEffect(() => {
		const t = setInterval(() => setNow(new Date()), 1000);
		return () => clearInterval(t);
	}, []);

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				px: 2,
				py: 0.8,
				background: 'linear-gradient(90deg, #0d0f18 0%, #1a1d2e 100%)',
				borderBottom: '1px solid rgba(255,255,255,0.07)',
				flexShrink: 0,
			}}
		>
			{/* Left: station + date */}
			<Box>
				<Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.88rem', lineHeight: 1.2 }}>
					{station?.name || 'Monitoramento de Quadro'}
				</Typography>
				<Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', textTransform: 'capitalize' }}>
					{dayjs(date).format('dddd, DD [de] MMMM [de] YYYY')}
				</Typography>
			</Box>

			{/* Center: countdown */}
			<Box sx={{ textAlign: 'center' }}>
				<Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.3 }}>
					próxima atualização
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Box sx={{ width: 80, height: 3, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
						<Box sx={{ height: '100%', width: `${(countdown / 60) * 100}%`, bgcolor: countdown <= 10 ? orange[400] : '#7869DF', borderRadius: 99, transition: 'width 1s linear' }} />
					</Box>
					<Typography sx={{ color: countdown <= 10 ? orange[400] : 'rgba(255,255,255,0.4)', fontWeight: 600, fontSize: '0.9rem', fontVariantNumeric: 'tabular-nums', minWidth: 28 }}>
						{countdown}s
					</Typography>
				</Box>
			</Box>

			{/* Right: clock + exit */}
			<Box display="flex" alignItems="center" gap={2}>
				<Typography
					sx={{
						color: 'white',
						fontWeight: 300,
						fontSize: '1.5rem',
						fontVariantNumeric: 'tabular-nums',
						letterSpacing: '0.04em',
						lineHeight: 1,
					}}
				>
					{now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
				</Typography>
				<Tooltip title="Sair do Modo TV (ESC)">
					<IconButton
						onClick={onExit}
						sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}
					>
						<FullscreenExitIcon />
					</IconButton>
				</Tooltip>
			</Box>
		</Box>
	);
};

// ─────────────────────────────────────────
// Main component
// ─────────────────────────────────────────

const AttendantGoalBoardMonitor = (props: any) => {
	const placeId = getPlaceId();
	const { permissions } = usePermissions();
	const refresh = useRefresh();
	const isAdmin = !!(permissions?.includes(PERMISSIONS.ADMIN));
	const isAdminRef = useRef(isAdmin);
	isAdminRef.current = isAdmin;

	const dataProvider = useDataProvider();
	// Link com posto escolhido (ex.: depois de ativar a degustação). Usuário de posto não troca de posto.
	const [searchParams] = useSearchParams();
	const linkedPlaceId = placeId ? '' : parsePlaceId(searchParams.get('placeId'));
	const linkedBoards = parseBoardIds(searchParams.get('boards'));
	const linkedBoardsKey = linkedBoards.join(',');
	const linkedTv = !!linkedPlaceId && searchParams.get('tv') === '1';

	const [date, setDate] = useState(dayjs().startOf('day').toISOString());
	const [chainId, setChainId] = useState<string>(getChainId() || '');
	const [stationId, setStationId] = useState<string>(placeId || linkedPlaceId || '');
	const [addedBoards, setAddedBoards] = useState<string[]>(() => {
		// Link com posto: a primeira tela já sai com os quadros do link (ou vazia, até a entrada automática), nunca com os de outra sessão.
		if (linkedPlaceId) return linkedBoards.slice(0, MAX_BOARDS);
		try { return JSON.parse(localStorage.getItem('monitorBoards') || '[]'); } catch { return []; }
	});
	const [showWithoutProgress, setShowWithoutProgress] = useState(false);
	const [isTvMode, setIsTvMode] = useState(false);
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [countdown, setCountdown] = useState(60);

	// Chains (admin only)
	const { data: chains } = useGetList(
		'chains',
		{ pagination: { page: 1, perPage: 1000 }, sort: { field: 'name', order: 'ASC' } },
		{ enabled: isAdmin },
	);

	// Stations
	const { data: stations } = useGetList(
		'stations',
		{
			pagination: { page: 1, perPage: 1000 },
			filter: getEmployeeType() === EmployeeType.attendant
				? { attendantId: getAttendantId(), chainId }
				: { chainId },
			sort: { field: 'name', order: 'ASC' },
		},
		{ enabled: !placeId },
	);

	// Boards data for TV mode day-of-week filtering
	const { data: boardsForFilter } = useGetList(
		'attendant-goal-boards',
		{
			filter: { chainId },
			pagination: { page: 1, perPage: 100 },
			sort: { field: 'name', order: 'ASC' },
		},
		{ enabled: !!chainId && addedBoards.length > 0 },
	);

	const tvVisibleBoards = addedBoards.filter(boardId => {
		if (!boardsForFilter) return true;
		const board = boardsForFilter.find((b: any) => b.id === boardId);
		if (!board) return true;
		const dayKey = DAY_SCHEDULE_KEYS[new Date().getDay()];
		return board[dayKey];
	});

	// Persist boards
	useEffect(() => {
		localStorage.setItem('monitorBoards', JSON.stringify(addedBoards));
	}, [addedBoards]);

	// TV mode: countdown + auto-refresh
	useEffect(() => {
		if (!isTvMode) return;
		setCountdown(60);
		const interval = setInterval(() => {
			setCountdown(prev => {
				if (prev <= 1) {
					if (!isAdminRef.current) setDate(dayjs().startOf('day').toISOString());
					refresh();
					return 60;
				}
				return prev - 1;
			});
		}, 1000);
		return () => clearInterval(interval);
	}, [isTvMode, refresh]);

	// Sync fullscreen exit (ESC)
	useEffect(() => {
		const onFsChange = () => { if (!document.fullscreenElement) setIsTvMode(false); };
		document.addEventListener('fullscreenchange', onFsChange);
		return () => document.removeEventListener('fullscreenchange', onFsChange);
	}, []);

	// Tela cheia é um extra: iPhone e navegador de TV antigo não têm a API (chamar direto lançaria erro e derrubaria a tela).
	const handleEnterTvMode = () => {
		document.documentElement.requestFullscreen?.()?.catch(() => {});
		setIsTvMode(true);
	};

	const handleExitTvMode = () => {
		if (document.fullscreenElement) document.exitFullscreen?.()?.catch(() => {});
		setIsTvMode(false);
	};

	// ESC também sai do Modo TV sem tela cheia (aberto por link, o navegador nega a tela cheia: falta um clique do usuário).
	// Com tela cheia, o navegador já avisa pelo `fullscreenchange` acima.
	useEffect(() => {
		if (!isTvMode) return undefined;
		const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') handleExitTvMode(); };
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTvMode]);

	const handleAddBoard = (boardId: string) => {
		if (addedBoards.length < MAX_BOARDS && !addedBoards.includes(boardId)) {
			setAddedBoards(prev => [...prev, boardId]);
		}
	};

	// Ao escolher o posto, os quadros dele entram sozinhos (de 1 a 4; com mais, o usuário escolhe em "Adicionar Quadro").
	// A mesma consulta do AddBoardDialog (posto + dia): só entra o que o diálogo listaria.
	const pickSeq = useRef(0);
	const autoSelectBoards = async (station: string) => {
		const seq = ++pickSeq.current; // escolha mais nova invalida a anterior (e '' cancela)
		if (!station || !chainId) return;
		try {
			const { data } = await dataProvider.getList('attendant-goal-boards', {
				filter: { stationId: station, date, chainId },
				pagination: { page: 1, perPage: 100 },
				sort: { field: 'name', order: 'ASC' },
			});
			const ids = autoSelectBoardIds(data);
			if (seq === pickSeq.current && ids) setAddedBoards(ids);
		} catch { /* sem entrada automática: "Adicionar Quadro" segue funcionando */ }
	};

	// Link do monitor: o posto já vem escolhido. Com `boards`, mostra exatamente esses quadros; sem, vale a regra de até 4.
	// A primeira tela já nasce do link (estado inicial); aqui entra a regra de até 4 e um link novo com a tela já aberta.
	const appliedLink = useRef('');
	useEffect(() => {
		if (!linkedPlaceId) return;
		const key = `${linkedPlaceId}|${linkedBoardsKey}`;
		if (appliedLink.current === key) return;
		appliedLink.current = key;
		setStationId(linkedPlaceId);
		if (linkedBoards.length > 0) {
			setAddedBoards(linkedBoards.slice(0, MAX_BOARDS));
		} else {
			setAddedBoards([]);
			autoSelectBoards(linkedPlaceId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [linkedPlaceId, linkedBoardsKey]);

	// `tv=1`: abre no Modo TV assim que o usuário se mostra com permissão de monitor (o overlay do TV não passa pelo <Permission>).
	const tvLinkApplied = useRef(false);
	useEffect(() => {
		if (!linkedTv || tvLinkApplied.current || !permissions?.includes(PERMISSIONS.ATTENDANT_TARGET_MONITOR)) return;
		tvLinkApplied.current = true;
		handleEnterTvMode();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [linkedTv, permissions]);

	const handleChainChange = (newChainId: string) => {
		setChainId(newChainId);
		setStationId('');
		setAddedBoards([]);
		autoSelectBoards('');
	};

	const handleStationChange = (newStationId: string) => {
		setStationId(newStationId);
		setAddedBoards([]);
		autoSelectBoards(newStationId);
	};

	// ── TV Mode (portal over everything) ──────────────────
	if (isTvMode) {
		return createPortal(
			<Box sx={{ position: 'fixed', inset: 0, zIndex: 9999, bgcolor: '#0d0f18', display: 'flex', flexDirection: 'column' }}>
				<TvHeader date={date} stationId={stationId} onExit={handleExitTvMode} countdown={countdown} />
				<Box sx={{ flex: 1, display: 'flex', gap: 2, p: 2, overflow: 'hidden' }}>
					{tvVisibleBoards.length === 0 ? (
						<Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							<Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '1.1rem' }}>
								{addedBoards.length > 0 ? 'Nenhum quadro programado para hoje' : 'Nenhum quadro adicionado'}
							</Typography>
						</Box>
					) : (
						tvVisibleBoards.map(boardId => (
							<BoardPanel
								key={boardId}
								boardId={boardId}
								stationId={stationId}
								date={date}
								chainId={chainId}
								showWithoutProgress={showWithoutProgress}
								isTvMode={true}
							/>
						))
					)}
				</Box>
			</Box>,
			document.body,
		);
	}

	// ── Normal View ────────────────────────────────────────
	return (
		<Permission permission={PERMISSIONS.ATTENDANT_TARGET_MONITOR}>
			<Box sx={{ p: 2 }}>
				<Title title="Monitoramento de quadro" />

				{/* ── Filters card ── */}
				<Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
					<Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
						{/* Date — admin only */}
						{isAdmin && (
							<TextField
								type="date"
								label="Data"
							sx={{ width: 155 }}
								size="small"
								value={dayjs(date).format('YYYY-MM-DD')}
								onChange={e => { if (e.target.value) setDate(dayjs(e.target.value).startOf('day').toISOString()); }}
								InputLabelProps={{ shrink: true }}
							/>
						)}

						{/* Chain — admin only */}
						{isAdmin && (
							<FormControl size="small" sx={{ width: 180 }}>
								<InputLabel>Rede</InputLabel>
								<Select value={chainId} label="Rede" onChange={e => handleChainChange(e.target.value)}>
									<MenuItem value="">Selecione uma rede</MenuItem>
									{(chains || []).map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
								</Select>
							</FormControl>
						)}

						{/* Station */}
						{!placeId && (
							<FormControl size="small" sx={{ width: 210 }} disabled={isAdmin && !chainId}>
								<InputLabel>Posto</InputLabel>
								<Select value={stationId} label="Posto" onChange={e => handleStationChange(e.target.value)}>
									<MenuItem value="">Selecione um posto</MenuItem>
									{(stations || []).map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
								</Select>
							</FormControl>
						)}

						{/* Show without progress */}
						<FormControlLabel
							control={<Switch checked={showWithoutProgress} onChange={e => setShowWithoutProgress(e.target.checked)} size="small" />}
							label={<Typography variant="body2" sx={{ fontSize: '0.82rem' }}>Mostrar sem progresso</Typography>}
							sx={{ ml: 0 }}
						/>

						<Box sx={{ flexGrow: 1 }} />

						{/* Add board */}
						<Button
							variant="outlined"
							startIcon={<AddIcon />}
							onClick={() => setAddDialogOpen(true)}
							disabled={addedBoards.length >= MAX_BOARDS || !stationId}
							size="small"
						>
							Adicionar Quadro
							{addedBoards.length > 0 && ` (${addedBoards.length}/${MAX_BOARDS})`}
						</Button>

						{/* TV mode */}
						<Button
							variant="contained"
							startIcon={<TvIcon />}
							onClick={handleEnterTvMode}
							disabled={addedBoards.length === 0}
							size="small"
							sx={{ bgcolor: '#7869DF', '&:hover': { bgcolor: '#5e55c4' } }}
						>
							Modo TV
						</Button>
					</Box>
				</Card>

				{/* ── Panels ── */}
				{addedBoards.length === 0 ? (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							minHeight: 280,
							border: '2px dashed',
							borderColor: 'grey.200',
							borderRadius: 2,
							gap: 1.5,
						}}
					>
						<Typography color="text.secondary" sx={{ fontSize: '0.95rem' }}>
							Nenhum quadro adicionado
						</Typography>
						<Button
							variant="outlined"
							startIcon={<AddIcon />}
							onClick={() => setAddDialogOpen(true)}
							disabled={!stationId}
							size="small"
						>
							{stationId ? 'Adicionar Quadro' : 'Selecione um posto primeiro'}
						</Button>
					</Box>
				) : (
					<Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', minHeight: 'calc(100vh - 200px)' }}>
						{addedBoards.map(boardId => (
							<BoardPanel
								key={boardId}
								boardId={boardId}
								stationId={stationId}
								date={date}
								chainId={chainId}
								showWithoutProgress={showWithoutProgress}
								onRemove={() => setAddedBoards(prev => prev.filter(id => id !== boardId))}
								isTvMode={false}
							/>
						))}
					</Box>
				)}

				<AddBoardDialog
					open={addDialogOpen}
					onClose={() => setAddDialogOpen(false)}
					onAdd={handleAddBoard}
					stationId={stationId}
					date={date}
					chainId={chainId}
					addedBoardIds={addedBoards}
				/>
			</Box>
		</Permission>
	);
};

export default AttendantGoalBoardMonitor;
